/**
 * 폼 공통 styled-components
 *
 * 폼 입력 패턴을 통합하여 중복을 제거한다.
 * - Inputs.tsx (InputWrapper, StyledInput, Label 등)
 * - PWChangeModal.tsx (InputWrapper)
 * - SocialSignUpModal.tsx (InputWrapper)
 * - VerificationModal.tsx (CodeInputContainer)
 *
 * 사용 예시:
 * <FormField>
 *   <FormLabel>이메일</FormLabel>
 *   <FormInput placeholder="이메일 입력" />
 *   <FormError>유효하지 않은 이메일</FormError>
 * </FormField>
 */

import styled, { css } from 'styled-components';

// ─────────────────────────────────────────────────────────────
// 타입 정의
// ─────────────────────────────────────────────────────────────

export type InputState = 'default' | 'error' | 'success' | 'disabled';
export type InputSize = 'sm' | 'md' | 'lg';

interface FormInputProps {
  state?: InputState;
  size?: InputSize;
  hasButton?: boolean;
}

// ─────────────────────────────────────────────────────────────
// 폼 필드 컨테이너
// ─────────────────────────────────────────────────────────────

/**
 * 폼 필드 래퍼 (간격·위치 제공)
 * 대체: Inputs.tsx InputWrapper, PWChangeModal.tsx InputWrapper, SocialSignUpModal.tsx InputWrapper
 */
export const FormField = styled.div`
  width: 100%;
  margin-top: 32px;
  position: relative;

  &:first-child {
    margin-top: 0;
  }
`;

/** 간격이 좁은 폼 필드 */
export const FormFieldCompact = styled(FormField)`
  margin-top: 16px;
`;

/** 가로 배치 폼 필드 */
export const FormFieldInline = styled(FormField)`
  margin-top: 0;
  display: flex;
  align-items: center;
  gap: 16px;
`;

// ─────────────────────────────────────────────────────────────
// 폼 레이블
// ─────────────────────────────────────────────────────────────

/**
 * 폼 레이블
 * 대체: Inputs.tsx Label
 */
export const FormLabel = styled.label`
  position: absolute;
  top: -32px;
  left: 0px;
  padding: 0 4px;

  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.fontBlackText};

  /* 입력 필드와 겹치지 않도록 이벤트 차단 */
  pointer-events: none;
`;

/** 인라인 레이블 (체크박스, 라디오 버튼용) */
export const FormLabelInline = styled(FormLabel)`
  position: static;
  pointer-events: auto;
  cursor: pointer;
`;

/** 선택 사항 레이블 표시 */
export const FormLabelOptional = styled.span`
  font-weight: 400;
  color: ${({ theme }) => theme.colors.fontGraySecondary};
  margin-left: 4px;

  &::before {
    content: '(';
  }

  &::after {
    content: ')';
  }
`;

// ─────────────────────────────────────────────────────────────
// 폼 입력 필드
// ─────────────────────────────────────────────────────────────

/**
 * 상태(state) variant를 지원하는 기본 입력 필드
 * 대체: Inputs.tsx StyledInput
 */
export const FormInput = styled.input<FormInputProps>`
  width: 100%;
  padding: ${({ size = 'md' }) =>
    size === 'sm' ? '8px 12px' : size === 'lg' ? '16px 20px' : '12px 16px'};
  padding-right: ${({ hasButton }) => (hasButton ? '90px' : '16px')};

  border-radius: 10px;
  border: 1px solid
    ${({ state = 'default', theme }) => {
      switch (state) {
        case 'error':   return theme.colors.errorRed;
        case 'success': return theme.colors.successGreen;
        default:        return theme.colors.borderGray;
      }
    }};

  background: ${({ state = 'default', theme }) =>
    state === 'success'
      ? theme.colors.bgWhite
      : theme.colors.bgGray?.[100] || theme.colors.backgroundSecondary};

  font-size: 16px;
  font-family: ${({ theme }) => theme.fonts.pretendard};
  color: ${({ theme }) => theme.colors.fontBlackText};

  transition: all 0.2s ease;

  &:focus {
    border-color: ${({ state = 'default', theme }) =>
      state === 'error' ? theme.colors.errorRed : theme.colors.successGreen};
    outline: none;
    background: ${({ theme }) => theme.colors.bgWhite};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.fontGraySecondary};
  }

  &:disabled {
    background: ${({ state = 'default', theme }) =>
      state === 'success'
        ? theme.colors.bgWhite
        : theme.colors.bgGray?.[200] || theme.colors.backgroundTertiary};
    cursor: ${({ state = 'default' }) => (state === 'success' ? 'default' : 'not-allowed')};
    opacity: ${({ state = 'default' }) => (state === 'success' ? 1 : 0.6)};
  }
`;

/** 텍스트에어리어 variant */
export const FormTextarea = styled(FormInput).attrs({ as: 'textarea' })`
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
`;

// ─────────────────────────────────────────────────────────────
// 버튼 내장 입력 필드 (이메일 인증 패턴)
// ─────────────────────────────────────────────────────────────

/** 액션 버튼이 포함된 입력 컨테이너 */
export const InputWithButton = styled.div`
  position: relative;
  width: 100%;
`;

/**
 * 입력 필드 내부 액션 버튼
 * 대체: Inputs.tsx ActionButton
 */
export const InputActionButton = styled.button<{ $isSent?: boolean; $isVerified?: boolean }>`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);

  padding: 8px 16px;

  background: ${({ theme, $isSent, $isVerified }) =>
    $isVerified
      ? theme.colors.bgWhite || theme.colors.successGreen
      : $isSent
        ? theme.colors.bgGray || theme.colors.bgWhite
        : theme.colors.bgWhite};

  color: ${({ theme }) => theme.colors.fontBlackText};
  border: 1px solid ${({ theme }) => theme.colors.borderGray};
  border-radius: 6px;

  font-size: 14px;
  font-weight: 600;

  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    background: ${({ theme, $isVerified }) =>
      $isVerified
        ? theme.colors.bgWhite || theme.colors.successGreen
        : theme.colors.btnDisabled};
    cursor: ${({ $isVerified }) => ($isVerified ? 'default' : 'not-allowed')};
    opacity: ${({ $isVerified }) => ($isVerified ? 1 : 0.7)};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

// ─────────────────────────────────────────────────────────────
// 비밀번호 입력 패턴
// ─────────────────────────────────────────────────────────────

/**
 * 비밀번호 입력 컨테이너 (토글 버튼 포함)
 * 대체: Inputs.tsx PasswordInputContainer
 */
export const PasswordInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

/**
 * 비밀번호 표시/숨김 토글 버튼
 * 대체: Inputs.tsx ToggleButton
 */
export const PasswordToggleButton = styled.button`
  position: absolute;
  right: 16px;

  padding: 4px 8px;

  background: none;
  border: none;

  font-size: 14px;
  color: ${({ theme }) => theme.colors.primary};

  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

// ─────────────────────────────────────────────────────────────
// 폼 메시지 (에러 / 성공 / 도움말)
// ─────────────────────────────────────────────────────────────

/** 에러 메시지 */
export const FormError = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.errorRed};
  margin-top: 8px;
  margin-bottom: 0;

  display: flex;
  align-items: center;
  gap: 4px;

  &::before {
    content: '⚠';
  }
`;

/**
 * 성공 메시지
 * 대체: Inputs.tsx EmailSuccessText
 */
export const FormSuccess = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.successGreen || theme.colors.primary};
  margin-top: 8px;
  margin-bottom: 0;

  display: flex;
  align-items: center;
  gap: 4px;

  &::before {
    content: '✓';
    font-weight: bold;
  }
`;

/** 도움말 텍스트 */
export const FormHelp = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.fontGraySecondary};
  margin-top: 8px;
  margin-bottom: 0;
`;

// ─────────────────────────────────────────────────────────────
// 인증 코드 입력 패턴 (OTP)
// ─────────────────────────────────────────────────────────────

/**
 * 코드 입력 컨테이너 (OTP, 인증 코드)
 * 대체: VerificationModal.tsx CodeInputContainer
 */
export const CodeInputContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;
`;

/** 코드 한 자리 입력 필드 */
export const CodeInput = styled.input`
  width: 48px;
  height: 56px;

  text-align: center;
  font-size: 24px;
  font-weight: 600;

  border: 2px solid ${({ theme }) => theme.colors.borderGray};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgWhite};

  color: ${({ theme }) => theme.colors.fontBlackText};

  transition: all 0.2s ease;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    outline: none;
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.bgGray?.[200] || theme.colors.backgroundSecondary};
    opacity: 0.6;
  }

  /* 숫자 입력 스피너 제거 */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type='number'] {
    -moz-appearance: textfield;
  }
`;

// ─────────────────────────────────────────────────────────────
// 폼 레이아웃
// ─────────────────────────────────────────────────────────────

/** 여러 필드를 세로로 나열하는 그룹 */
export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

/** 가로 배치 폼 행 */
export const FormRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

/** 폼 행 안의 열 */
export const FormColumn = styled.div<{ flex?: number }>`
  flex: ${({ flex = 1 }) => flex};
  min-width: 0; /* flex 항목 overflow 방지 */
`;

// ─────────────────────────────────────────────────────────────
// 체크박스 / 라디오
// ─────────────────────────────────────────────────────────────

/** 체크박스·라디오 컨테이너 */
export const FormCheck = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

/** 숨겨진 체크박스·라디오 input */
export const FormCheckInput = styled.input`
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
`;

/** 커스텀 체크박스·라디오 박스 */
export const FormCheckBox = styled.span<{ checked?: boolean }>`
  width: 20px;
  height: 20px;

  border: 2px solid ${({ theme, checked }) => (checked ? theme.colors.primary : theme.colors.borderGray)};
  border-radius: 4px;

  background: ${({ theme, checked }) => (checked ? theme.colors.primary : 'transparent')};

  display: flex;
  align-items: center;
  justify-content: center;

  transition: all 0.2s ease;

  &::after {
    content: '✓';
    color: white;
    font-size: 14px;
    font-weight: bold;
    opacity: ${({ checked }) => (checked ? 1 : 0)};
    transition: opacity 0.2s ease;
  }
`;

// ─────────────────────────────────────────────────────────────
// 폼 제출 영역
// ─────────────────────────────────────────────────────────────

/** 폼 액션 컨테이너 (제출 버튼 등) */
export const FormActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 32px;

  @media (max-width: 768px) {
    flex-direction: column-reverse;

    button {
      width: 100%;
    }
  }
`;

export default {
  FormField,
  FormFieldCompact,
  FormFieldInline,
  FormLabel,
  FormLabelInline,
  FormLabelOptional,
  FormInput,
  FormTextarea,
  InputWithButton,
  InputActionButton,
  PasswordInputContainer,
  PasswordToggleButton,
  FormError,
  FormSuccess,
  FormHelp,
  CodeInputContainer,
  CodeInput,
  FormGroup,
  FormRow,
  FormColumn,
  FormCheck,
  FormCheckInput,
  FormCheckBox,
  FormActions,
};
